import { useCallback, useMemo, type ComponentProps } from 'react';
import type { RunStore } from '@inference.engine/agentic-react';
import {
  CompactionMarkers,
  markersByMessage,
  useConversationSummaries,
} from '@/features/agent-builder';
import { ChatThread } from '@/features/chats';

type ThreadWithCompactionsProps = ComponentProps<typeof ChatThread> & {
  chatId: string;
  runStore: RunStore;
};

/** The thread with a marker after each turn a summary of the conversation ends on. */
export function ThreadWithCompactions({ chatId, runStore, ...thread }: ThreadWithCompactionsProps) {
  // The server has no conversation to read summaries of until its first turn ends.
  const hasStoredTurn = thread.messages.some(
    (message) => message.type === 'agent' && !message.isStreaming,
  );
  const summaries = useConversationSummaries(chatId, runStore, hasStoredTurn);
  const markers = useMemo(
    () => markersByMessage(thread.messages, summaries),
    [thread.messages, summaries],
  );
  const renderAfterMessage = useCallback(
    (index: number) => {
      const here = markers.get(index);
      return here ? <CompactionMarkers summaries={here} /> : null;
    },
    [markers],
  );
  return <ChatThread {...thread} renderAfterMessage={renderAfterMessage} />;
}
